import React from 'react';
import { Box, Button, Divider, Drawer, FormControlLabel, IconButton, Switch, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

interface FormDrawerProps {
    open: boolean;
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    saveLabel: string;
    isSaving: boolean;
    saveDisabled?: boolean;
    onClose: () => void;
    onSave: () => void;
    children: React.ReactNode;
}

// Padrão de edição do módulo: drawer à direita com cabeçalho, conteúdo rolável e rodapé fixo
export function FormDrawer({ open, icon, title, subtitle, saveLabel, isSaving, saveDisabled, onClose, onSave, children }: FormDrawerProps) {
    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={isSaving ? undefined : onClose}
            ModalProps={{ sx: { zIndex: 1300 } }}
            PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 680 }, display: 'flex', flexDirection: 'column' } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', p: 1, borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    {icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap>{title}</Typography>
                    {subtitle && <Typography variant="caption" color="text.secondary" noWrap component="div">{subtitle}</Typography>}
                </Box>
                <IconButton onClick={onClose} disabled={isSaving} aria-label="Fechar">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {children}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                <Button variant="outlined" startIcon={<CancelIcon />} onClick={onClose} disabled={isSaving}>Cancelar</Button>
                <LoadingButton variant="contained" startIcon={<SaveIcon />} onClick={onSave} loading={isSaving} disabled={isSaving || saveDisabled}>
                    {saveLabel}
                </LoadingButton>
            </Box>
        </Drawer>
    );
}

export function FormDrawerGroup({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600 }}>{title}</Typography>
                {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
                <Divider sx={{ mt: 1 }} />
            </Box>
            {children}
        </Box>
    );
}

// Liga/desliga com título e explicação abaixo (padrão para campos booleanos nos drawers)
export function SwitchField({ label, description, checked, onChange, disabled }: {
    label: string; description?: string; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean;
}) {
    return (
        <FormControlLabel
            control={<Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />}
            label={
                <Box>
                    <Typography variant="body2" fontWeight={500}>{label}</Typography>
                    {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
                </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0, '& .MuiSwitch-root': { mt: -0.5 } }}
        />
    );
}

export function VisibilitySwitch({ checked, onChange, description, disabled }: { checked: boolean; onChange: (checked: boolean) => void; description: string; disabled?: boolean }) {
    return <SwitchField label="Visível no portal" description={description} checked={checked} onChange={onChange} disabled={disabled} />;
}
